import type { App } from 'vue'

import type { RbacOptions, RbacPlugin, RuleSink } from './types/runtime'

import Can from './components/TwnCan.vue'
import { createController } from './controller'
import { createVCan } from './directives/vCan'
import { RBAC_KEY } from './keys'
import { abilitiesResponseSchema } from './schema/abilitiesResponse'

/**
 * Plugin factory. The returned plugin:
 *  - provides the controller under {@link RBAC_KEY},
 *  - registers `<TwnCan>` and `v-can`,
 *  - wires connectivity tracking,
 *  - starts the transport with a Zod-validating sink (the single validation
 *    choke point — transports stay dumb and just hand over raw bytes).
 */
export const createRbac = (options: RbacOptions): RbacPlugin => {
  const controller = createController(options)

  const sink: RuleSink = {
    update(payload) {
      const parsed = abilitiesResponseSchema.safeParse(payload)

      if (!parsed.success) {
        // Keep the prior ability; never widen access on a malformed payload.
        options.onInvalidPayload?.(parsed.error)
        controller.setStatus('error')
        return
      }

      controller.applyResponse(parsed.data)
      controller.setStatus('ready')
    },
    clear() {
      controller.clear()
    },
    setStatus(status) {
      controller.setStatus(status)
    }
  }

  const install = (app: App): void => {
    app.provide(RBAC_KEY, controller)
    app.component('TwnCan', Can)
    app.directive('can', createVCan(controller))

    if (typeof window !== 'undefined') {
      const goOnline = (): void => {
        controller.online.value = true
      }
      const goOffline = (): void => {
        controller.online.value = false
      }

      window.addEventListener('online', goOnline)
      window.addEventListener('offline', goOffline)

      // Torn down with the app, not left on `window`. The host app installs once
      // per page so this rarely fires in production — but a test suite that
      // mounts with `withRbac()` installs a fresh plugin per test, and without
      // this each one leaves two listeners pinning a dead controller.
      app.onUnmount(() => {
        window.removeEventListener('online', goOnline)
        window.removeEventListener('offline', goOffline)
        options.transport.stop?.()
      })
    }

    void options.transport.start(sink)
  }

  return { install, controller }
}
